<template>
  <div>
    <div>
      <!-- Sidebar Toggle -->
      <b-sidebar
        id="sidebar-backdrop"
        ref="sidebar"
        :backdrop-variant="variant"
        :backdrop="!pinned"
        shadow
        class="left-nav-sidebar"
        :visible="sidebarOpened"
        no-header
        :no-close-on-backdrop="pinned"
        :no-close-on-route-change="pinned"
        @hidden="onSidebarHidden"
      >
        <div class="sidebar-wrapper">
          <div class="sidebar-header">
            <b-button
              variant="link"
              class="pin-button"
            >
              <font-awesome-icon
                icon="thumbtack"
                :class="['pin-icon', pinned ? 'pinned' : 'unpinned']"
                @click="togglePinned"
              />
            </b-button>
          </div>
          <template v-if="activeContent === 'search'">
            <div>
              <h3 id="sidebar-no-header-title">
                Search
              </h3>
              <div class="sticky-header">
                <b-form-input
                  ref="searchInput"
                  v-model="searchKeyword"
                  class="search-input"
                  type="text"
                  placeholder="Search"
                  data-test="search-input"
                  debounce="500"
                />
              </div>
            </div>
            <div
              v-if="!!searchKeyword.length"
              class="menu"
            >
              <div
                class="search-subgroup"
                @click="toggleTableSearch()"
              >
                MDM Tables
                <font-awesome-icon
                  icon="chevron-right"
                  :class="['chevron', tablesOpened && 'opened']"
                />
              </div>
              <ul
                class="menu-list"
                data-test="mdm-menu-list"
                :class="[tablesOpened ? 'show opened' : 'hide']"
              >
                <sidebar-item
                  v-for="(menuItem, index) in filteredItems('tables')"
                  :key="index"
                  data-test="mdm-menu-item"
                  :item="menuItem"
                  :search-value="searchKeyword"
                  :is-search-value-exist="!!searchKeyword.length"
                  :hide-children="true"
                />
              </ul>
              <div
                class="search-subgroup"
                @click="toggleTagSearch()"
              >
                MDM Tags
                <font-awesome-icon
                  icon="chevron-right"
                  :class="['chevron', tagsOpened && 'opened']"
                />
              </div>
              <ul
                class="menu-list"
                data-test="mdm-menu-list"
                :class="[tagsOpened ? 'show' : 'hide']"
              >
                <sidebar-item
                  v-for="(menuItem, index) in filteredItems('tags')"
                  :key="index"
                  data-test="mdm-menu-item"
                  :item="menuItem"
                  :search-value="searchKeyword"
                  :is-search-value-exist="!!searchKeyword.length"
                  :hide-children="true"
                />
              </ul>
              <div
                class="search-subgroup"
                @click="toggleModuleSearch()"
              >
                MDM Modules
                <font-awesome-icon
                  icon="chevron-right"
                  :class="['chevron', modulesOpened && 'opened']"
                />
              </div>
              <ul
                class="menu-list"
                data-test="mdm-menu-list"
                :class="[modulesOpened ? 'show' : 'hide']"
              >
                <sidebar-item
                  v-for="(menuItem, index) in filteredItems('modules')"
                  :key="index"
                  data-test="mdm-menu-item"
                  :item="menuItem"
                  :search-value="searchKeyword"
                  :is-search-value-exist="!!searchKeyword.length"
                  :hide-children="true"
                />
              </ul>
              <div
                class="search-subgroup"
                @click="toggleFolderSearch()"
              >
                MDM Folders
                <font-awesome-icon
                  icon="chevron-right"
                  :class="['chevron', foldersOpened && 'opened']"
                />
              </div>
              <ul
                class="menu-list"
                data-test="mdm-menu-list"
                :class="[foldersOpened ? 'show' : 'hide']"
              >
                <sidebar-item
                  v-for="(menuItem, index) in filteredItems('folders')"
                  :key="index"
                  data-test="mdm-menu-item"
                  :item="menuItem"
                  :search-value="searchKeyword"
                  :is-search-value-exist="!!searchKeyword.length"
                  :hide-children="true"
                />
              </ul>
            </div>
          </template>
          <template v-else-if="activeContent === 'systemModules'">
            <h3 id="sidebar-no-header-title">
              System modules
            </h3>
            <div
              v-if="systemItems.length"
              class="menu"
            >
              <ul
                class="menu-list"
                data-test="system-menu-list"
              >
                <sidebar-item
                  v-for="(menuItem, index) in systemItems"
                  :key="index"
                  :item="menuItem"
                  data-test="system-menu-item"
                  :search-value="searchKeyword"
                  :is-search-value-exist="!!searchKeyword.length"
                />
              </ul>
            </div>
          </template>
          <template v-else-if="activeContent === 'custom'">
            <h3 id="sidebar-no-header-title">
              {{ customItemSelected.name }}
            </h3>
            <div
              class="menu"
            >
              <ul
                class="menu-list"
                data-test="mdm-menu-list"
              >
                <sidebar-item
                  v-for="(subItem, index) in mdmItems"
                  :key="index"
                  :custom="true"
                  data-test="mdm-menu-item"
                  :item="subItem"
                />
              </ul>
            </div>
          </template>
          <template v-else-if="activeContent === 'user'">
            <h3 id="sidebar-no-header-title">
              User
            </h3>
  
            <ul
              class="menu-list"
              data-test="general-menu-list"
            >
              <li>Profile</li>
              <li
                href="#"
                @click="onLogoutClick()"
              >
                Sign Out
              </li>
              <li>Version {{ version }} <br><small>{{ versionDate | toDatetime }}</small></li>
            </ul>
          </template>
        </div>
      </b-sidebar>
  
      <!-- Left Navigation -->
      <div class="left-nav">
        <b-button
          title="Data Quality"
          :to="{name: 'dataQuality'}"
          class="navbar-brand-img"
          :style="{ marginBottom: avatarStyle.gap}"
        >
          <b-img src="/assets/img/png-logo.png" />
        </b-button>
        <div 
          class="icon-menu" 
          :style="{ gap: avatarStyle.gap}"
        >
          <b-button
            variant="link"
            title="Search"
            :class="{ 'active-icon': activeContent === 'search' }"
            @click="changeContent('search')"
          >
            <i
              class="fa fa-search"
              :style="{ fontSize: avatarStyle.icon}"
            />
          </b-button>
          <b-button
            variant="link"
            title="System Modules"
            :class="{ 'active-icon': activeContent === 'systemModules' && $route.name !== 'dataCatalog' && !sidebarOpened }"
            @click="changeContent('systemModules')"
          >
            <i
              class="fa-brands fa-ubuntu"
              :style="{ fontSize: avatarStyle.icon}"
            />
          </b-button>
          <b-button
            id="data-catalog-left-nav"
            variant="link"
            :to="{name: 'dataCatalog'}"
            title="Data Catalog"
            :class="{ 'active-icon': $route.name === 'dataCatalog' && !sidebarOpened }"
            @click="changeContent('dataCatalog')"
          >
            <i
              class="fa fa-book"
          
              :style="{ fontSize: avatarStyle.icon}"
            />
          </b-button>
          <b-button
            v-for="item in leftNavData?.filter(x=>x.visible)"
            variant="link"
            :title="item.name"
            :class="{ 'active-icon': customItemSelected?.id === item.id && $route.name !== 'dataCatalog' && !sidebarOpened }"
            @click="changeContent('custom', item)"
          >
            <i 
              :class="['fa', `fa-${item.icon}`]"
              :style="{ fontSize: avatarStyle.icon}"
            />
          </b-button>
        </div>
        <b-button
          variant="link"
          class="user-button"
          @click="changeContent('user')"
        >
          <b-img
            rounded="circle"
            class="user-avatar"
            :style="{ width: avatarStyle.avatar, height: avatarStyle.avatar }"
            :src="avatar"
            alt="User Avatar"
          />
        </b-button>
      </div>
    </div>
  </div>
</template>
  
  
<script>
import SidebarItem from './SidebarItem';
  
import { createNamespacedHelpers } from 'vuex';
import auth from '../../services/auth.service';
import { axiosClient } from '../../../core/services/http.service';

const MENU_PINNED_LS_KEY = 'menuPin';
const {  mapActions } = createNamespacedHelpers('app');
const {
  mapState: dataManagementMapState,
} = createNamespacedHelpers('dataManagement');
const { mapState } = createNamespacedHelpers('user');
const {
  mapState: userMapState,
  mapActions: mapActionsUser,
} = createNamespacedHelpers('user');
const {
  mapActions: mapActionsSidebar,
  mapState: mapStateSidebar,
} = createNamespacedHelpers('sidebar');
const {
  mapState: LeftNavCustomizationMapState,
  mapActions: LeftNavCustomizationMapActions,
} = createNamespacedHelpers('leftNavCustomization');
  
export default {
  name: 'SidebarNew',
  components: {
    SidebarItem,
  },
  data: () => {
    return {
      pinned: false,
      searchKeyword: '',
      showSystemModules: false,
      showMDMModules: true,
      isCollapsed: false,
      version: null,
      versionDate: null,
      variant: 'dark',
      activeContent: '',
      sidebarOpened: false,
      tablesOpened: true,
      foldersOpened: true,
      modulesOpened: true,
      tagsOpened: true,
      leftNavCustomization: null,
      customItemSelected: null,
      customSubItemsToShow: null,
    };
  },
  watch: {
    sidebarOpened(val){
      if(!val){
        this.searchKeyword = '';
        this.tagsOpened = true;
        this.tablesOpened = true;
        this.modulesOpened = true;
        this.foldersOpened = true;
      }
    },
  },
  computed: {
    avatarStyle() {
      const count = this.leftNavData?.filter(x=>x.visible).length;
      let fontSize = 22;
      let logo = 40;
      let avatar = 40;
      let gap = 20;

      if (count > 10 && count <= 15) {
        fontSize = 18;
        logo = 36;
        avatar = 36;
        gap = 10;
      } else if (count > 15) {
        fontSize = 10;
        logo = 26;
        avatar = 26;
        gap = 5;
      }

      // return { avatar: `${avatar}`, icon: `${fontSize}`, logo: `${logo}` };
      return { icon: `${fontSize}px`, avatar:`${avatar}px`, logo: `${logo}px`, gap: `${gap}px`};
    },
    ...mapState(['user']),
    ...LeftNavCustomizationMapState({
      leftNavData: state=>state.leftNavData,
    }),
    ...userMapState({
      currentUserPermission: state => state.currentUserAdpPermission.data.role,
      avatar() {
        return this.user.avatar || '/img/avatar.svg';
      },
    }),
    ...dataManagementMapState({
      permissionTypes: state => state.permissionTypes,
    }),
    ...mapStateSidebar({
      sidebarItems: state => state.menuItems,
      foldersList: state => state.foldersList,
      modulesList: state => state.modulesList,
      tablesList: state => state.tablesList,
      foldersExceptions: state => state.foldersListWithExceptions,
    }),
    systemItems() {
      if (!this.sidebarItems) return [];
  
      const systemPathsNames = {
        'Mdm Model': '/app/mdm-model',
        'To Do List': '/app/todolist',
        'Connectors': '/app/connector-screen',
        'Loan Calculator': '/app/loan-calculator',
        'Sun Model': '/app/sunmodel',
        'Orchestration': '/app/orchestration',
        'File Logs': '/monitoring/file-logs',
        'Job Queue': '/monitoring/job-queue', // Add the path to the job queue module
        'FTP Storage List': '/file-manipulation/storage-list',
        'Batch File': '/file-manipulation/batch-file',
        'Batch Endpoint': '/file-manipulation/batch-endpoint',
        'User Security': '/administration/users',
        'Audit Log': '/administration/audit-log',
        'System Settings': '/administration/system-settings',
        'Global Permissions': '/administration/global',
        'Custom navigation': '/administration/custom-navigation',
      };
      const list = this.sidebarItems
        .filter(item => item.isSystem)
        .map(item => {
          if ('children' in item) {
            item.children = item.children.map(folder => {
              //this block is there for loancalculator, as child elements are tables, but not shown in navigation - it will produce an error lacking icon
              if ('children' in folder) {
                folder.children = [];
              }
              return {
                ...folder,
                icon: folder.icon || ['fas', 'folder'],
                type: 'folder',
                isSelected: false,
                path: folder.type === 'sub-module' ? folder.path : null,
              };
            });
          }
          return {
            ...item,
            type: 'module',
            icon: item.icon || ['fas', 'puzzle-piece'],
            path: systemPathsNames[item.label] ? `${systemPathsNames[item.label]}` : null,
          };
        });
      list.unshift(
        {
          label: 'Module Configuration',
          type: 'module',
          icon: ['fas', 'puzzle-piece'],
          path: '/app/all-modules',
        },
      );
      return list;
    },
  
    mdmItems() {
      if (!this.customSubItemsToShow) return [];

      var processItem = (item, inheritedModuleGuid = null) =>{
        // Determine if the item is a module, folder, or entity
        const isEntity = !!item.entityGuid;
        const isFolder = !!item.folderGuid && !isEntity;
        const isModule = !!item.moduleGuid && !isFolder && !isEntity;
          
        let path = '';
        let icon = item.icon;
        let type = '';
        const moduleGuid = item.moduleGuid || inheritedModuleGuid; 

        if (isModule) {
          path = `/app/all-modules/${moduleGuid}`;
          icon = icon || ['fas', 'puzzle-piece'];
          type = 'module';
        } 
        else if (isFolder) {
          path = `/app/all-modules/${moduleGuid}`;
          icon = icon || ['fas', 'folder'];
          type = 'folder';
        } 
        else if (isEntity) {
          const entityConfig = JSON.parse(item.entityConfig || '{}');
          const isPowerApp = entityConfig.EntityType === 'powerApp';
          const isPowerBiReport = entityConfig.EntityType === 'powerBiReport';

          const view = isPowerApp
            ? 'power-apps-table'
            : isPowerBiReport
              ? 'power-bi-report'
              : 'table';

          path = `/app/all-modules/${moduleGuid || 'null'}/${view}/${item.entityGuid}?moduleUuid=${moduleGuid || 'null'}`;
          icon = icon || ['fas', 'table'];
          type = 'table';
        }

        const children = (item.children || [])
          .filter(el => !['none', 'deny'].includes(el.currentUserPermissionLevel))
          .map(child => processItem(child, moduleGuid)) // Recursively apply full processing
          .filter(Boolean); // Remove null values from skipped items

        return {
          ...item,
          type,
          icon,
          path,
          moduleGuid,
          isSelected: false,
          children, // Fully processed children
        };};
      return this.customSubItemsToShow
        .map(item => processItem(item));
    },
  },
  methods: {
    ...LeftNavCustomizationMapActions(['getLeftNavData']),
    ...mapActions(['toggleMenuCollapse', 'toggleMenuPinned']),
    ...mapActionsSidebar(['getSidebarStructure', 'transformData']),
    ...mapActionsUser(['getUserAdGroups']),
    togglePinned() {
      this.pinned = !this.pinned;
      this.toggleMenuPinned(this.pinned);
      localStorage.setItem(MENU_PINNED_LS_KEY, this.pinned);

      if (this.pinned) {
        localStorage.setItem(MENU_PINNED_LS_KEY, this.pinned);
        this.sidebarOpened = true;
      } else {
        localStorage.removeItem(MENU_PINNED_LS_KEY);
      }
    },
    goTo(moduleGuid){
      this.$router.push(`/app/all-modules/${moduleGuid}`);
    },
    onSidebarHidden() {
      if (!this.pinned) {
        this.sidebarOpened = false;
      }
    },
    onLogoutClick() {
      auth.logout();
    },
    toggleTableSearch(){
      this.tablesOpened = !this.tablesOpened;
    },
    toggleFolderSearch(){
      this.foldersOpened = !this.foldersOpened;
    },
    toggleModuleSearch(){
      this.modulesOpened = !this.modulesOpened;
    },
    toggleTagSearch(){
      this.tagsOpened = !this.tagsOpened;
    },
    changeContent(content, customItem = null) {
      const selectedCustomItem = this.customItemSelected == customItem;
      if(customItem){
        this.customItemSelected = customItem;
        this.getSubItems(customItem);
      }
      else{
        this.customItemSelected = null;
      }
      if (!this.pinned) {
        if (content === this.activeContent && this.sidebarOpened) {
          if(selectedCustomItem)
            this.sidebarOpened = false;
        } else {
          this.activeContent = content;
          this.sidebarOpened = true;
        } 
      } else {
        this.activeContent = content;
      }
      //focus search input
      if(content==='search'){
        //wait a tick, so search input is actually visible
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
      if(content==='dataCatalog' && !this.pinned){
        if(this.sidebarOpened){
          this.sidebarOpened = false;
        }
      }
    },
    getSubItems(item){
      this.customSubItemsToShow = item.subItems.map(subItem => {
        if (subItem.moduleGuid) {
          return this.modulesList[subItem.moduleGuid];
        } else if (subItem.folderGuid) {
          return this.foldersList[subItem.folderGuid];
        } else if (subItem.entityGuid) {
          return this.tablesList[subItem.entityGuid];
        }
        return null; // If none exist
      }).filter(Boolean); // Remove null values
    },
    filteredItems(type, tags = false) {
      let objects;
      switch(type){
      case 'modules':
        objects = this.modulesList;
        break;
      case 'folders':
        objects = this.foldersList;
        break;
      case 'tables':
      case 'tags':
        objects = this.tablesList;
        break;
      }
      var values = Object.values(objects);
  
      const searchKeyword = this.searchKeyword.toString().toLowerCase();
      const isSearchValueExist = !!searchKeyword.length;
  
        
      if (!values || !isSearchValueExist) return [];
  
      const backupMenuItems = JSON.parse(JSON.stringify(values));
  
      function searchRecursive(item) {
          
        let matchesSearchTerm = item.label.toLowerCase().includes(searchKeyword);
        if(type === 'tags'){
          matchesSearchTerm = item.tags.some(tag => tag.tagName.toLowerCase().includes(searchKeyword));
        }
        if (item.label === 'Loan Calculator' && isSearchValueExist && matchesSearchTerm) {
          return;
        }
        //let hasVisibleChildren = false;
        // if (item.children) {
        //   item.children = item.children
        //     .filter(child => searchRecursive(child))
        //     .map(child => ({ ...child, isOpen: isSearchValueExist }));
        //   hasVisibleChildren = item.children.length > 0;
        // }
        return matchesSearchTerm;
      }
  
      const filteredMenuItems = backupMenuItems
        .filter((item) => searchRecursive(item))
        .map((item) => ({ ...item, isOpen: isSearchValueExist }))
        .sort((a, b) => a.sortOrder - b.sortOrder);
  
      // const whatToReturn = transformData(
      //   filteredMenuItems,
      //   isSearchValueExist,
      //   this.foldersExceptions,
      // );
      switch(type){
      case 'modules':
      case 'folders':
        return filteredMenuItems.map(folder => {
          return {
            ...folder,
            type: 'folder',
            isSelected: false,
            icon: folder.icon || ['fas', 'folder'],
            path: `/app/all-modules/${folder.moduleGuid}`,
          };
        });
      case 'tables':
      case 'tags':
        return filteredMenuItems.map(entity => {
          const isPowerApp = JSON.parse(entity.entityConfig).EntityType === 'powerApp';
          const isPowerBiReport = JSON.parse(entity.entityConfig).EntityType === 'powerBiReport';
          const view = isPowerApp
            ? 'power-apps-table'
            : isPowerBiReport
              ? 'power-bi-report'
              : 'table';
          return {
            ...entity,
            type: 'table',
            isSelected: false,
            icon: entity.icon || ['fas', 'table'],
            path: `/app/all-modules/${entity.moduleGuid}/${view}/${entity.entityGuid}?moduleUuid=${entity.moduleGuid}`,
          };
        });
      }
      return [];
    },
  },
  created() {
    this.getUserAdGroups();
  },
  async beforeMount() {
    if (!this.sidebarItems) {
      await this.getSidebarStructure();
    }
  },
  async mounted() {
    this.pinned = !!localStorage.getItem(MENU_PINNED_LS_KEY);
    this.toggleMenuPinned(!!this.pinned);
    this.sidebarOpened = !!this.pinned;
    this.$route.path.includes('all-modules') 
      ? this.activeContent = 'mdmModules'
      : this.activeContent = 'systemModules';

    const { success, body } = await axiosClient.get('/api/version');
    if (success) {
      this.version = `${body.version}`;
      this.versionDate = `${body.updateDate}`;
    }
    await this.getLeftNavData();
  },
};
</script>
  
<style lang="scss">
.menu,
  
  ul {
    margin-bottom: 0;
    list-style: none !important;
    padding-left: 0 !important;
  }
  
  .menu,
  ul:has(.fourth-version){
    background-color: #fff;

    & > :not(:last-child) {
        border-bottom: 2px solid #e9ecf1;
    }
  }

  .sidebar-wrap {
    border-right: 2px solid #d5d5d5;
    background-color: #473030;
    display: flex;
    flex-direction: column;
    position: absolute;
  }
  
  .sidebar {
    display: flex;
    flex-direction: column;
  }
  
  .sticky-header {
    flex-shrink: 0;
    padding: 10px;
  }
  
  .search-input {
    border-radius: unset;
    display: block;
    height: 42px;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    border-top: 1px solid gainsboro;
  
    &:focus {
      border-color: unset;
    }
  }
  
  .collapsed-button {
    position: absolute;
    top: 50%;
    right: -17px;
    transform: translateY(-50%);
    z-index: 1000;
    padding: 5px 2px;
    border: none;
    background: #0F355E;
    color: #fff;
    font-size: 18px;
    transition: all 0.3s;
    border-radius: 2px;
  
    &:hover {
      background-color: #285291;
      color: white;
    }
  }
  
  .menu-section-title {
    margin: 0;
    padding: 5px 15px;
    background-color: #0F355E;
    color: white;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: 0.3s;
  
    &:first-child {
      border-bottom: 1px solid white;
    }
  
    &:hover {
      background-color: #285291;
    }
  }
  
  .btn .btn:focus,
  .clear__btn {
    box-shadow: none;
    color: white;
  }
  
  .left-nav {
    .btn:not(.user-button){
      width: 56px;
    }
    .btn:hover:not(.user-button:hover) {
      background: linear-gradient(to right, #49617c, #17283B);
    }
    .active-icon:not(.user-button:hover){
      background: linear-gradient(to right, #49617c, #17283B);
    }
  }
  
  .chevron {
    color: rgb(23, 40, 59);
    transition: 0.3s;
  
    &.opened {
      transform: rotate(90deg);
    }
  }
  
  .user-avatar {
    width: 32px;
  }
  
  .user-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  
  .dropdown-menu-outside {
    position: absolute;
    top: 48px !important;
    left: 205px !important;
    transform: translateX(10px);
    min-width: 150px;
    z-index: 1050;
    background-color: #ffffff;
    border: 1px solid #ddd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  .logo-container {
    background-color: #0f355e !important;
    display: flex;
    justify-content: center;
    padding: 5px;
  }
  
  .logo {
    width: 180px;
  }
  
  #user-dropdown__BV_toggle_:hover {
    text-decoration: none !important;
  }
  
  .left-nav {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    height: 100vh;
    width: 60px;
    background-color: rgb(23, 40, 59);
    padding: 10px 0;
    color: white;
    z-index: 20000000;
    position: fixed;
  }
  
  .left-nav b-button,
  .left-nav .user-avatar {
    margin: 10px 0;
    /* Add spacing between buttons */
  }
  
  .left-nav .user-avatar {
    /* Adjust avatar size */
    //scale this
    width: 40px;
    height: 40px;
    display: block;
  }
  
  .fa,
  .fa-brands {
    color: white;
    font-size: 22px;//scale this
  }
  
  .btn.user-button {
    padding: 0;
    border-top: 3px solid #F6931E !important;
    width: 100%;
    display: flex;
    padding-top: 10px;
    justify-content: center;
  }
  
  .user-avatar {
    width: 40px;
    /* Adjust size as needed */
    height: 40px;
    display: block;
  }
  
  .left-nav-sidebar {
    .b-sidebar:not(.b-sidebar-right) {
      left: 61px !important;
      width: 380px;
    }
  }
  
  .b-sidebar-body {
    h3 {
      padding: 15px 15px 5px 10px;
    }
  }
  .icon-menu{
    flex-grow: 1;
    display:flex;
    flex-direction: column;
    align-items: center;
    gap:20px;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
  }
  .left-nav{
    .user-button:hover, .navbar-brand-img:hover{
      background-color: transparent;
    }
  }
  .navbar-brand-img{
    background-color: transparent!important;
    border-color: transparent!important;
    img{
      width: 30px;//scale this
    }
  }
  .sidebar-wrapper{
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100vh;
    &>:last-child{
      overflow-y: auto;
      height: 100%;
    }
  }
  .search-subgroup{
    padding: 4px;
    padding-right: 10px;
    font-size: 14px;
    background-color: #17283B;
    color: white;
    border-left: 2px solid #F6931E;
    border-bottom: 2px solid #F6931E;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    & > .chevron{
      color:white;
    }
  }
  .show {
    opacity: 1;
    height: fit-content;
  }
  .hide{
    display: none;
  }
  .sidebar-header{
    position: absolute;
    right: 0;
  }
  .pin-icon {
  transition: transform 0.3s ease;
}

.unpinned {
  transform: rotate(45deg); 
}

.pinned {
  transform: rotate(0deg); 
}

</style>
  